'use client'

import { DEFAULT_LOCATION_NAME } from './customer.constants'

import type { OnboardingState } from '@/components/common/Onboarding/Onboarding.types'
import type { DropdownOptionsType } from '@/components/primitives/Dropdown/Dropdown'
import type { Customer } from '@/types/shopify/customer-account'
import type { AccountState } from '@/types/shopify/customer-account'

// Mirrors @shopify/hydrogen-react's internal CART_ID_STORAGE_KEY (not exported publicly).
// CartProvider persists the cart id here; see node_modules/@shopify/hydrogen-react .../CartProvider.js
const CART_ID_STORAGE_KEY = 'shopifyCartId'

/**
 * Removes the Hydrogen cart id from localStorage so the cart does not persist
 * between users. Required because the cart is not associated with a customer
 * and carries a location-based buyerIdentity for pricing.
 */
export const clearCartStorage = () => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CART_ID_STORAGE_KEY)
  } catch {
    // localStorage unavailable (private mode / blocked) — nothing to clear
  }
}

/**
 * @description Account Type and Account State get updated in the mutation under OnboardingProvider.
 * @param customer
 * @returns
 */
export const getCustomerState = (customer: Customer): OnboardingState => {
  const accountState = customer?.accountState?.value as AccountState
  const hasLocations = Boolean(customer?.companyContacts?.nodes?.[0]?.company?.locations?.nodes?.find((location) => location?.name !== DEFAULT_LOCATION_NAME))

  // Commercial and Domestic accounts are onboarded if they have locations and an account number
  if (accountState === 'Onboarded' && hasLocations) {
    return 'ONBOARDED'
  }

  // B2B accounts are B2B onboarding if they have a B2B onboarding information
  if (accountState === 'B2B Pending') {
    return 'B2B_ONBOARDING'
  }

  return 'NOT_ONBOARDED'
}

/**
 * Get address options for the Contract Pricing
 * This returns the options for the Dropdown component
 * This is used only when a customer is logged in and has been onboarded (B2B or D2C)
 */
export const getLocations = (
  customer: Customer,
): { locationOptions: DropdownOptionsType; locationMap: Map<string, { postcode: string; displayAddress: string; name: string }> } => {
  if (!customer) return { locationOptions: [], locationMap: new Map() }

  // We have to filter out by default location name
  // This is created in Shopify Flow when a company is created
  // We are creating a company + location everytime a customer is created
  // Limitation with Shopify: Company that must contain AT LEAST one location
  const locations =
    customer?.companyContacts?.nodes?.flatMap((contact) => contact.company?.locations?.nodes ?? [])?.filter((location) => location?.name !== DEFAULT_LOCATION_NAME) ?? []

  const locationOptions: DropdownOptionsType = []

  const locationMap = new Map<string, { postcode: string; displayAddress: string; name: string }>()

  locations.forEach((location) => {
    const postcode = location?.shippingAddress?.zip ?? ''

    const displayAddress = `${location.shippingAddress?.address1 ?? ''} ${location.shippingAddress?.city ?? ''} ${postcode}`
    const locationName = `${location.externalId ? `${location.externalId} - ` : ''}${location.name}`
    locationOptions.push({
      label: locationName,
      value: location.id,
    })
    locationMap.set(location.id, { postcode, displayAddress, name: locationName })
  })

  return { locationOptions, locationMap }
}
